/**
 * dogBehaviour.ts — Pure, Phaser-free helpers for the dog's behaviour.
 *
 * Extracted from Dog.ts so the state→speed mapping can be unit-tested without a
 * physics body. See config/constants.ts → DOG for the tunable speeds.
 */
import { DOG } from '../config/constants';
import { lungeSpeed } from '../systems/frenzy';
import { DogState } from '../types';

/**
 * How fast the dog wants to move in a given state (px/s). This is the magnitude
 * the player feels through the leash — HYPER_PULL is the hardest yank, and both
 * poop states root the dog in place.
 *
 * HYPER_PULL is the one state that isn't a single number: the dog hauls harder
 * the closer the critter is, so `pull` (0..1, from systems/frenzy.ts →
 * `critterPull`) slides it between `LUNGE_MIN_SPEED` and `HYPER_SPEED`. Every
 * other state ignores the argument.
 *
 * NEEDS_GRASS pulls as hard as a distraction, and deliberately: a dog that
 * needs to go is *determined*, and the point of the state is that you feel it
 * dragging you toward the park rather than reading about it in the HUD.
 *
 * SNIFFING is the other half of that idea, and it goes the opposite way: the
 * few seconds before the urge lands, the dog dawdles. A lead that has gone
 * slack and heavy is the earliest warning the player gets, and it arrives
 * through the same channel as everything else the dog is feeling.
 */
export function dogSpeedForState(state: DogState, pull = 1): number {
  switch (state) {
    case DogState.HYPER_PULL:
      return lungeSpeed(pull, DOG.LUNGE_MIN_SPEED, DOG.HYPER_SPEED);
    case DogState.DISTRACTED:
    case DogState.NEEDS_GRASS:
      return DOG.DISTRACTED_SPEED;
    case DogState.SNIFFING:
      return DOG.SNIFF_SPEED;
    case DogState.POOPING:
    case DogState.POOP_PENDING:
      return 0;
    default: // CALM, CLEANED
      return DOG.CALM_SPEED;
  }
}
